<!--
  RichTextToolbar.vue

  Barra de ferramentas do RichTextEditor. Separada do editor de propósito: permite
  presets diferentes depois sem duplicar a instância do Tiptap.

  ⚠️ REGRA QUE GOVERNA ESTE ARQUIVO: só pode existir botão para formatação que
  sobreviva ao normalizador do backend. Botão para o que a whitelist descarta é
  mentira — o usuário formata, salva e perde SEM NENHUM ERRO, porque o editor
  guarda o estado em memória e a perda só aparece no recarregamento.

  Cada controle daqui passou pela suite de contrato
  (`tests/editor-contract/` + `HtmlProjectionCheck`). Antes de acrescentar botão,
  acrescente o caso lá e confirme que ele sobrevive.

  Ausências que são decisão, não esquecimento:
   • código inline e bloco de código — a whitelist desembrulha <code>
   • cor de texto e de fundo — a Regra 2 mata `style`
   • largura de coluna de tabela — mesmo motivo (exceção declarada na suite)
   • upload de imagem — o botão de imagem pede ENDEREÇO porque a imagem é
     hotlink: não há onde guardar binário. Ver `LitteraeImage`
   • redimensionar imagem — gravaria width/height, que a whitelist não aceita;
     o tamanho é do CSS de `.text-body--html img`
   • negrito/centralização da legenda — vêm do CSS de `.text-body--html
     figcaption`. Aparência é decisão de leitura, e gravá-la faria toda legenda
     carregar formatação que o usuário não escolheu e não pode tirar
-->
<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  ALIGN_CLASSES,
  FONT_FAMILY_CLASSES,
  FONT_SIZE_CLASSES,
  findAnchorTarget,
  isAllowedImageSrc,
} from "@/utils/tiptapEditorConfig";
import RichTextFormatMenu from "./RichTextFormatMenu.vue";

const props = defineProps({
  editor: { type: Object, default: null },
  disabled: { type: Boolean, default: false },
});

const HEADING_LEVELS = [1, 2, 3, 4, 5, 6];

// A primeira opção é sempre o padrão: é ela que o RichTextSelect mostra quando o
// valor corrente não casa com nenhuma.
const BLOCK_OPTIONS = [
  { value: "paragraph", label: "Parágrafo" },
  ...HEADING_LEVELS.map((level) => ({
    value: `h${level}`,
    label: `Título ${level}`,
  })),
];

// Rótulos das opções. As CHAVES vêm do vocabulário compartilhado — se uma classe
// for adicionada ou removida lá, aqui quebra na cara em vez de divergir calado.
const ALIGNMENTS = [
  { value: "left", icon: "fa-align-left", label: "Alinhar à esquerda" },
  { value: "center", icon: "fa-align-center", label: "Centralizar" },
  { value: "right", icon: "fa-align-right", label: "Alinhar à direita" },
  { value: "justify", icon: "fa-align-justify", label: "Justificar" },
];

const FONT_FAMILIES = [
  { value: "", label: "Fonte padrão" },
  { value: "serif", label: "Serifada" },
  { value: "sans", label: "Sem serifa" },
  { value: "mono", label: "Monoespaçada" },
];

const FONT_SIZES = [
  { value: "", label: "Corpo padrão" },
  { value: "sm", label: "Menor" },
  { value: "lg", label: "Maior" },
];

const currentBlock = computed(() => {
  const editor = props.editor;
  if (!editor) return "paragraph";
  for (const level of HEADING_LEVELS) {
    if (editor.isActive("heading", { level })) return `h${level}`;
  }
  return "paragraph";
});

const currentFamily = computed(() => {
  const editor = props.editor;
  if (!editor) return "";
  return (
    Object.keys(FONT_FAMILY_CLASSES).find((key) =>
      editor.isActive("textStyle", { fontFamily: key })
    ) ?? ""
  );
});

const currentSize = computed(() => {
  const editor = props.editor;
  if (!editor) return "";
  return (
    Object.keys(FONT_SIZE_CLASSES).find((key) =>
      editor.isActive("textStyle", { fontSize: key })
    ) ?? ""
  );
});

const isInTable = computed(() => props.editor?.isActive("table") ?? false);

function isActive(...args) {
  return props.editor?.isActive(...args) ?? false;
}

function isAlignActive(value) {
  // "left" é o padrão e não tem classe: fica ativo quando nenhum outro está.
  if (value === "left") {
    return !Object.keys(ALIGN_CLASSES).some((key) =>
      props.editor?.isActive({ textAlign: key })
    );
  }
  return props.editor?.isActive({ textAlign: value }) ?? false;
}

function run(command) {
  const chain = props.editor?.chain().focus();
  if (chain) command(chain).run();
}

function applyBlock(value) {
  if (value === "paragraph") return run((c) => c.setParagraph());
  run((c) => c.setHeading({ level: Number(value.slice(1)) }));
}

function applyFamily(value) {
  if (!value) return run((c) => c.unsetFontFamily());
  run((c) => c.setFontFamily(value));
}

// Os três grupos do menu de formato, na ordem em que aparecem. A `key` é o que
// volta no evento e decide o comando — o rótulo é só o que se lê.
const formatGroups = computed(() => [
  { key: "block", label: "Bloco", modelValue: currentBlock.value, options: BLOCK_OPTIONS },
  { key: "family", label: "Fonte", modelValue: currentFamily.value, options: FONT_FAMILIES },
  { key: "size", label: "Corpo", modelValue: currentSize.value, options: FONT_SIZES },
]);

function applyFormat({ key, value }) {
  if (key === "block") return applyBlock(value);
  if (key === "family") return applyFamily(value);
  if (key === "size") return applySize(value);
}

function applySize(value) {
  if (!value) return run((c) => c.unsetFontSize());
  run((c) => c.setFontSize(value));
}

// ── Link ────────────────────────────────────────────────────────────────────
// Fluxo completo: inserir, editar e remover. O esquema é validado aqui com as
// MESMAS regras do normalizador — sem isso a interface aceitaria um link que o
// backend descartaria depois, e o usuário só descobriria no recarregamento.
const showLinkPanel = ref(false);
const linkUrl = ref("");
const linkInput = ref(null);
const linkPanelEl = ref(null);
const linkButtonEl = ref(null);

/**
 * Fecha o painel a qualquer clique que não seja nele nem no botão que o abre.
 *
 * Cobre os dois casos de uma vez — clicar noutro botão da barra e clicar no
 * texto —, e não depende de eventos do Tiptap. É o mesmo padrão dos demais
 * dropdowns do projeto.
 *
 * O botão do link precisa da exceção: sem ela, o próprio clique que abre o painel
 * chegaria aqui e o fecharia no mesmo gesto.
 */
function onDocumentClick(event) {
  if (showLinkPanel.value &&
      !linkPanelEl.value?.contains(event.target) &&
      !linkButtonEl.value?.contains(event.target)) {
    showLinkPanel.value = false;
  }

  if (showImagePanel.value &&
      !imagePanelEl.value?.contains(event.target) &&
      !imageButtonEl.value?.contains(event.target)) {
    showImagePanel.value = false;
  }
}

onMounted(() => document.addEventListener("click", onDocumentClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocumentClick));

const ALLOWED_SCHEMES = ["http:", "https:", "mailto:"];

function validateUrl(value) {
  const url = value.trim();
  if (!url) return "Informe um endereço.";
  if (url.startsWith("#")) return url.length > 1 ? "" : "Âncora vazia.";

  const scheme = url.slice(0, url.indexOf(":") + 1).toLowerCase();
  if (!ALLOWED_SCHEMES.includes(scheme)) {
    return "Use http, https, mailto ou uma âncora começando por #.";
  }
  return "";
}

// A validação passou a ser CONTÍNUA, e não só no clique.
//
// Os botões do painel nascem desligados e acendem quando o endereço é válido —
// foi o pedido. Mas botão desligado não explica por que está desligado, então o
// mesmo cálculo alimenta a mensagem: campo vazio não acusa nada (ainda não há o
// que criticar), campo preenchido e inválido diz o motivo.
const linkProblem = computed(() =>
  linkUrl.value.trim() ? validateUrl(linkUrl.value) : ""
);

const canApplyLink = computed(
  () => linkUrl.value.trim() !== "" && linkProblem.value === ""
);

// Âncora interna não abre em aba: `#trecho` é posição nesta página, e uma aba
// nova cairia no começo dela.
const canOpenLink = computed(
  () => canApplyLink.value && !linkUrl.value.trim().startsWith("#")
);

// …o que ela faz é saltar. Os dois botões ocupam o mesmo lugar e nunca aparecem
// juntos: um endereço é externo ou é âncora, não os dois.
const isAnchorLink = computed(() => linkUrl.value.trim().startsWith("#"));

/**
 * Rola o editor até o alvo da âncora.
 *
 * O mesmo que o clique no link já faz (ver `LitteraeLink`); existe aqui porque
 * o painel é o lugar onde a pessoa VÊ que aquele link é uma âncora, e um botão
 * desligado ao lado de um endereço válido lê como beco sem saída.
 */
const canGoToAnchor = computed(
  () =>
    isAnchorLink.value &&
    !!props.editor &&
    !!findAnchorTarget(props.editor.view.dom, linkUrl.value.trim())
);

function goToAnchor() {
  const target = findAnchorTarget(props.editor?.view?.dom, linkUrl.value.trim());
  if (!target) return;

  target.scrollIntoView({ block: "center", behavior: "smooth" });
  showLinkPanel.value = false;
}

const hasLinkOnSelection = computed(() => isActive("link"));

async function toggleLinkPanel() {
  if (props.disabled) return;
  if (showLinkPanel.value) {
    showLinkPanel.value = false;
    return;
  }
  linkUrl.value = props.editor?.getAttributes("link")?.href ?? "";
  showImagePanel.value = false;
  showLinkPanel.value = true;
  await nextTick();
  linkInput.value?.focus();
}

function applyLink() {
  if (!canApplyLink.value) return;
  run((c) => c.extendMarkRange("link").setLink({ href: linkUrl.value.trim() }));
  showLinkPanel.value = false;
}

// `noopener` fecha o acesso da aba nova ao `window.opener`; `noreferrer` evita
// vazar a URL de origem. Mesma regra que o renderizador aplica na leitura.
function openLinkInNewTab() {
  if (!canOpenLink.value) return;
  window.open(linkUrl.value.trim(), "_blank", "noopener,noreferrer");
}

function removeLink() {
  if (!hasLinkOnSelection.value) return;
  run((c) => c.extendMarkRange("link").unsetLink());
  showLinkPanel.value = false;
}

// ── Imagem ──────────────────────────────────────────────────────────────────
// Mesmo desenho do link, e pelo mesmo motivo: a validação de esquema acontece
// AQUI, com a função que o schema do editor e o normalizador do backend também
// usam (`isAllowedImageSrc`), para os três recusarem exatamente as mesmas URLs.
//
// ⚠️ Não há upload: a imagem é HOTLINK, o conteúdo guarda só o endereço. É a
// única rota enquanto não houver storage de mídia — ver o comentário do
// `LitteraeImage`. O campo pede endereço por isso, não por preguiça de interface.
const showImagePanel = ref(false);
const imageUrl = ref("");
const imageAlt = ref("");
const imageCaption = ref("");
const imageInput = ref(null);
const imagePanelEl = ref(null);
const imageButtonEl = ref(null);

// Distingue "o painel abriu porque clicaram na imagem" de "o painel abriu porque
// clicaram no botão". Só o primeiro se fecha sozinho quando a seleção sai da
// imagem — fechar o segundo apagaria o endereço que a pessoa está digitando para
// inserir uma imagem nova.
const imagePanelFromSelection = ref(false);

const imageProblem = computed(() => {
  const url = imageUrl.value.trim();
  if (!url) return "";
  return isAllowedImageSrc(url)
    ? ""
    : "Use o endereço http ou https de uma imagem.";
});

const canApplyImage = computed(
  () => imageUrl.value.trim() !== "" && imageProblem.value === ""
);

// Imagem solta e imagem legendada são nós DIFERENTES (`image` inline e `figure`
// bloco — ver `LitteraeFigure`), mas para a barra são a mesma coisa: uma imagem
// selecionada. Todo o painel trata os dois juntos.
const hasImageSelected = computed(() => isActive("image") || isActive("figure"));

/** Atributos da imagem selecionada, venha ela de qual dos dois nós for. */
function selectedImageAttributes() {
  const editor = props.editor;
  if (!editor) return {};
  if (editor.isActive("figure")) return editor.getAttributes("figure");
  if (editor.isActive("image")) return editor.getAttributes("image");
  return {};
}

function loadImageFields() {
  const current = selectedImageAttributes();
  imageUrl.value = current.src ?? "";
  imageAlt.value = current.alt ?? "";
  imageCaption.value = current.caption ?? "";
}

async function toggleImagePanel() {
  if (props.disabled) return;
  if (showImagePanel.value) {
    showImagePanel.value = false;
    return;
  }
  // Com uma imagem selecionada o painel abre para EDITAR, não para inserir outra.
  loadImageFields();
  imagePanelFromSelection.value = false;
  showLinkPanel.value = false;
  showImagePanel.value = true;
  await nextTick();
  imageInput.value?.focus();
}

/**
 * Clicar numa imagem já abre o painel preenchido — o pedido era entrar em modo de
 * edição no clique, sem passar pelo botão.
 *
 * ⚠️ **Sem roubar o foco.** Focar o campo tiraria o foco do ProseMirror, e com
 * ele o anel azul que marca a imagem selecionada: a pessoa clicaria na imagem e
 * veria a marcação sumir no mesmo gesto. Quem abre pelo BOTÃO recebe o foco
 * (ali não há seleção visual a preservar); quem abre pelo clique na imagem, não.
 */
watch(hasImageSelected, (selecionada) => {
  if (selecionada) {
    loadImageFields();
    imagePanelFromSelection.value = true;
    showLinkPanel.value = false;
    showImagePanel.value = true;
    return;
  }

  // Saiu da imagem: recolhe o painel que a própria seleção abriu. O painel aberto
  // pelo botão fica, porque ali há digitação em curso a preservar.
  if (imagePanelFromSelection.value) {
    showImagePanel.value = false;
    imagePanelFromSelection.value = false;
  }
});

function applyImage() {
  if (!canApplyImage.value) return;

  const src = imageUrl.value.trim();
  const alt = imageAlt.value.trim() || null;
  const caption = imageCaption.value.trim();

  // A legenda é o que decide a FORMA do conteúdo, não um enfeite: com legenda a
  // imagem vira `<figure>`, que é bloco e sai do parágrafo; sem legenda continua
  // inline. Trocar a legenda converte de um para o outro, e é por isso que os
  // dois caminhos passam por aqui em vez de um `updateAttributes`.
  //
  // `alt` e `caption` vazios viram null/"" e não chegam a virar atributo:
  // `alt=""` e `<figcaption></figcaption>` no conteúdo seriam ruído gravado.
  if (caption) {
    run((c) => c.setFigure({ src, alt, caption }));
  } else {
    run((c) => c.setImage({ src, alt }));
  }

  showImagePanel.value = false;
  imagePanelFromSelection.value = false;
}

function removeImage() {
  if (!hasImageSelected.value) return;
  run((c) => c.deleteSelection());
  showImagePanel.value = false;
  imagePanelFromSelection.value = false;
}
</script>

<template>
  <div v-if="editor" class="rte-toolbar-wrap">
    <div class="rte-toolbar" role="toolbar" aria-label="Formatação do texto">
      <!-- Bloco, família e corpo num menu só, com subníveis. Ver o comentário
           do componente para o porquê de estarem juntos. -->
      <RichTextFormatMenu
        :groups="formatGroups"
        :disabled="disabled"
        @select="applyFormat"
      />

      <span class="rte-divider" aria-hidden="true"></span>

      <button
        type="button" class="rte-btn"
        :class="{ 'rte-btn--active': isActive('bold') }"
        :disabled="disabled" title="Negrito (Ctrl+B)" aria-label="Negrito"
        @click="run((c) => c.toggleBold())"
      ><i class="fas fa-bold"></i></button>

      <button
        type="button" class="rte-btn"
        :class="{ 'rte-btn--active': isActive('italic') }"
        :disabled="disabled" title="Itálico (Ctrl+I)" aria-label="Itálico"
        @click="run((c) => c.toggleItalic())"
      ><i class="fas fa-italic"></i></button>

      <button
        type="button" class="rte-btn"
        :class="{ 'rte-btn--active': isActive('underline') }"
        :disabled="disabled" title="Sublinhado (Ctrl+U)" aria-label="Sublinhado"
        @click="run((c) => c.toggleUnderline())"
      ><i class="fas fa-underline"></i></button>

      <button
        type="button" class="rte-btn"
        :class="{ 'rte-btn--active': isActive('strike') }"
        :disabled="disabled" title="Tachado" aria-label="Tachado"
        @click="run((c) => c.toggleStrike())"
      ><i class="fas fa-strikethrough"></i></button>

      <button
        type="button" class="rte-btn"
        :class="{ 'rte-btn--active': isActive('superscript') }"
        :disabled="disabled" title="Sobrescrito" aria-label="Sobrescrito"
        @click="run((c) => c.toggleSuperscript())"
      ><i class="fas fa-superscript"></i></button>

      <button
        type="button" class="rte-btn"
        :class="{ 'rte-btn--active': isActive('subscript') }"
        :disabled="disabled" title="Subscrito" aria-label="Subscrito"
        @click="run((c) => c.toggleSubscript())"
      ><i class="fas fa-subscript"></i></button>

      <span class="rte-divider" aria-hidden="true"></span>

      <button
        v-for="alignment in ALIGNMENTS" :key="alignment.value"
        type="button" class="rte-btn"
        :class="{ 'rte-btn--active': isAlignActive(alignment.value) }"
        :disabled="disabled" :title="alignment.label" :aria-label="alignment.label"
        @click="run((c) => c.setTextAlign(alignment.value))"
      ><i class="fas" :class="alignment.icon"></i></button>

      <!-- Recuo de primeira linha. Vizinho do alinhamento porque é da mesma
           família: forma do parágrafo, não do caractere. -->
      <button
        type="button" class="rte-btn"
        :class="{ 'rte-btn--active': isActive('paragraph', { indent: true }) }"
        :disabled="disabled" title="Recuo de primeira linha"
        aria-label="Recuo de primeira linha"
        @click="run((c) => c.toggleIndent())"
      ><i class="fas fa-indent"></i></button>

      <span class="rte-divider" aria-hidden="true"></span>


      <button
        type="button" class="rte-btn"
        :class="{ 'rte-btn--active': isActive('bulletList') }"
        :disabled="disabled" title="Lista com marcadores" aria-label="Lista com marcadores"
        @click="run((c) => c.toggleBulletList())"
      ><i class="fas fa-list-ul"></i></button>

      <button
        type="button" class="rte-btn"
        :class="{ 'rte-btn--active': isActive('orderedList') }"
        :disabled="disabled" title="Lista numerada" aria-label="Lista numerada"
        @click="run((c) => c.toggleOrderedList())"
      ><i class="fas fa-list-ol"></i></button>

      <button
        type="button" class="rte-btn"
        :class="{ 'rte-btn--active': isActive('blockquote') }"
        :disabled="disabled" title="Citação" aria-label="Citação"
        @click="run((c) => c.toggleBlockquote())"
      ><i class="fas fa-quote-right"></i></button>

      <button
        type="button" class="rte-btn" :disabled="disabled"
        title="Linha horizontal" aria-label="Linha horizontal"
        @click="run((c) => c.setHorizontalRule())"
      ><i class="fas fa-minus"></i></button>

      <span class="rte-divider" aria-hidden="true"></span>

      <button
        type="button" class="rte-btn"
        :class="{
 'rte-btn--active': isActive('link'),
          'rte-btn--open': showLinkPanel,
        }"
        ref="linkButtonEl"
        :disabled="disabled" title="Link" aria-label="Inserir ou editar link"
        @click="toggleLinkPanel"
      ><i class="fas fa-link"></i></button>

      <button
        type="button" class="rte-btn"
        :class="{
 'rte-btn--active': hasImageSelected,
          'rte-btn--open': showImagePanel,
        }"
        ref="imageButtonEl"
        :disabled="disabled" title="Imagem" aria-label="Inserir ou editar imagem"
        @click="toggleImagePanel"
      ><i class="fas fa-image"></i></button>

      <button
        type="button" class="rte-btn" :disabled="disabled"
        title="Inserir tabela" aria-label="Inserir tabela"
        @click="run((c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }))"
      ><i class="fas fa-table"></i></button>

      <span class="rte-divider" aria-hidden="true"></span>

      <button
        type="button" class="rte-btn"
        :disabled="disabled || !editor.can().undo()"
        title="Desfazer (Ctrl+Z)" aria-label="Desfazer"
        @click="run((c) => c.undo())"
      ><i class="fas fa-undo"></i></button>

      <button
        type="button" class="rte-btn"
        :disabled="disabled || !editor.can().redo()"
        title="Refazer (Ctrl+Y)" aria-label="Refazer"
        @click="run((c) => c.redo())"
      ><i class="fas fa-redo"></i></button>

      <!-- O divisor fica AQUI, e não junto do botão, para herdar o escopo de
           estilo desta barra: conteúdo de slot é compilado no escopo de quem o
           fornece, e `.rte-divider` não o alcançaria de lá. -->
      <span class="rte-divider" aria-hidden="true"></span>

      <slot name="end" />
    </div>

    <!-- Painel do link: aparece só quando pedido, para não roubar espaço. -->
    <div v-if="showLinkPanel" ref="linkPanelEl" class="rte-panel">
      <input
        ref="linkInput"
        v-model="linkUrl"
        class="rte-input"
        type="text"
        placeholder="Cole um link…"
        aria-label="Endereço do link"
        @keydown.enter.prevent="applyLink"
        @keydown.esc.prevent="showLinkPanel = false"
      />

      <!--
        Três ícones no lugar dos três botões de texto: aplicar, abrir e remover.
        Nascem desligados e acendem conforme o estado — o de aplicar e o de abrir
        quando o endereço é válido, o de remover quando há link na seleção.
      -->
      <button
        type="button"
        class="rte-panel-icon"
        :disabled="!canApplyLink"
        title="Aplicar link (Enter)"
        aria-label="Aplicar link"
        @click="applyLink"
      ><i class="fas fa-level-down-alt fa-rotate-90"></i></button>

      <span class="rte-divider" aria-hidden="true"></span>

      <!-- Externo abre em aba; âncora salta. Nunca os dois, porque um endereço é
           um ou é outro — e o botão de abrir, desligado diante de uma âncora
           válida, lia como beco sem saída. -->
      <button
        v-if="!isAnchorLink"
        type="button"
        class="rte-panel-icon"
        :disabled="!canOpenLink"
        title="Abrir em nova aba"
        aria-label="Abrir link em nova aba"
        @click="openLinkInNewTab"
      ><i class="fas fa-external-link-alt"></i></button>

      <button
        v-else
        type="button"
        class="rte-panel-icon"
        :disabled="!canGoToAnchor"
        title="Ir para a âncora"
        aria-label="Ir para a âncora"
        @click="goToAnchor"
      ><i class="fas fa-anchor"></i></button>

      <button
        type="button"
        class="rte-panel-icon rte-panel-icon--danger"
        :disabled="!hasLinkOnSelection"
        title="Remover link"
        aria-label="Remover link"
        @click="removeLink"
      ><i class="fas fa-trash-alt"></i></button>

      <span v-if="linkProblem" class="rte-panel-error">{{ linkProblem }}</span>
    </div>

    <!--
      Painel da imagem: mesma forma do painel do link, com um campo a mais.

      O texto alternativo tem peso próprio aqui e não é enfeite de acessibilidade:
      sendo hotlink, é ele que aparece na tela quando o site de origem tira a
      imagem do ar.
    -->
    <div v-if="showImagePanel" ref="imagePanelEl" class="rte-panel">
      <input
        ref="imageInput"
        v-model="imageUrl"
        class="rte-input"
        type="text"
        placeholder="Cole o endereço da imagem…"
        aria-label="Endereço da imagem"
        @keydown.enter.prevent="applyImage"
        @keydown.esc.prevent="showImagePanel = false"
      />

      <!-- Sendo hotlink, o `alt` não é só acessibilidade: é o que sobra na tela
           quando o site de origem tira a imagem do ar. O título diz isso, que é
           o que faz alguém se dar ao trabalho de preencher. -->
      <input
        v-model="imageAlt"
        class="rte-input rte-input--alt"
        type="text"
        placeholder="Texto alternativo (opcional)"
        aria-label="Texto alternativo da imagem"
        title="Aparece no lugar da imagem se ela não carregar. Também é lido por leitores de tela."
        @keydown.enter.prevent="applyImage"
        @keydown.esc.prevent="showImagePanel = false"
      />

      <!-- A legenda muda a FORMA do conteúdo, não só a aparência: preenchida, a
           imagem vira uma figura em bloco e sai do parágrafo. Por isso o título
           avisa, em vez de deixar a conversão acontecer calada — e avisa sem a
           palavra "bloco", que é jargão de quem escreveu o editor, não de quem o
           usa. -->
      <input
        v-model="imageCaption"
        class="rte-input rte-input--alt"
        type="text"
        placeholder="Legenda (opcional)"
        aria-label="Legenda da imagem"
        title="Aparece em negrito abaixo da imagem, que passa a ocupar uma linha só dela."
        @keydown.enter.prevent="applyImage"
        @keydown.esc.prevent="showImagePanel = false"
      />

      <button
        type="button"
        class="rte-panel-icon"
        :disabled="!canApplyImage"
        title="Aplicar imagem (Enter)"
        aria-label="Aplicar imagem"
        @click="applyImage"
      ><i class="fas fa-level-down-alt fa-rotate-90"></i></button>

      <button
        type="button"
        class="rte-panel-icon rte-panel-icon--danger"
        :disabled="!hasImageSelected"
        title="Remover imagem"
        aria-label="Remover imagem"
        @click="removeImage"
      ><i class="fas fa-trash-alt"></i></button>

      <span v-if="imageProblem" class="rte-panel-error">{{ imageProblem }}</span>
    </div>

    <!-- Controles de tabela: só dentro de uma. -->
    <div v-if="isInTable" class="rte-panel rte-panel--table">
      <span class="rte-panel-label">Tabela:</span>
      <button type="button" class="rte-panel-btn" @click="run((c) => c.addRowAfter())">
        + linha
      </button>
      <button type="button" class="rte-panel-btn" @click="run((c) => c.deleteRow())">
        − linha
      </button>
      <button type="button" class="rte-panel-btn" @click="run((c) => c.addColumnAfter())">
        + coluna
      </button>
      <button type="button" class="rte-panel-btn" @click="run((c) => c.deleteColumn())">
        − coluna
      </button>
      <button
        type="button" class="rte-panel-btn rte-panel-btn--danger"
        @click="run((c) => c.deleteTable())"
      >Excluir tabela</button>
    </div>
  </div>
</template>

<style scoped>
/* A barra acompanha a rolagem: num texto longo, ela sairia da tela e o usuário
   ficaria sem formatação a meio caminho.

   `top` vem de uma variável para quem embute o editor poder descer a barra e não
   colidir com a própria faixa de ações — no leitor, os botões Salvar/Cancelar
   ocupam o topo. Fora desse caso a variável não existe e o padrão é 0.

   Duas variáveis governam a barra, e é o embutidor quem as define:
     --rte-toolbar-top           onde ela gruda      (padrão 0)
     --rte-toolbar-flow-height   altura no fluxo     (padrão auto; 0 = sobrepõe)

   O fundo precisa ser OPACO, senão o texto rola por baixo e aparece através dos
   botões. */
/* A altura do invólucro NO FLUXO é do embutidor, e o padrão é ocupar espaço.
   
   Sobrepor o texto (altura zero) é exigência do LEITOR: lá, entrar em edição não
   pode deslocar o conteúdo, senão quem estava lendo perde a linha. Mas essa
   exigência estava fixa aqui, e valia para todo mundo — inclusive para o editor
   dentro de um formulário, onde não há conteúdo a preservar e o efeito era só
   dano: a barra opaca cobria as primeiras linhas da área editável, e quem
   digitava não via o que estava digitando até passar delas. O contador contava
   certo o tempo todo, porque a digitação sempre chegou ao editor — só o texto
   ficava atrás da barra.

   Quem quer a sobreposição pede por ela, como já pede o `top`.

   O `sticky` funciona nos dois casos: com altura zero ele gruda na posição de
   fluxo do invólucro, que é o topo do editor. */
.rte-toolbar-wrap {
  position: sticky;
  top: var(--rte-toolbar-top, 0);
  height: var(--rte-toolbar-flow-height, auto);
  z-index: 4;
}

/* O fundo e a borda saíram do invólucro e vieram para a barra: é ela que flutua
   sobre o texto, e precisa ser opaca para o texto não aparecer por trás. */
/* `align-items` centra os itens DENTRO de cada linha; `align-content` centra o
   conjunto das linhas quando a barra quebra em mais de uma. Sem o segundo, o
   padrão é esticar as linhas, e o bloco de botões deixa de ficar centrado na
   barra assim que ela quebra — que é o caso em telas estreitas.

   O `padding-bottom` desconta a borda inferior: ela entra na altura visível da
   barra, então padding igual em cima e embaixo deixaria os botões 1px acima do
   centro óptico. */
.rte-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  gap: 0.15rem;
  padding: 0.35rem 0.5rem calc(0.35rem - 1px);
  background: var(--rte-bg, var(--surface));
  border-bottom: 1px solid rgba(128, 128, 128, 0.3);
  border-radius: 0.45rem 0.45rem 0 0;
}

.rte-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.4rem;
  border-radius: 0.35rem;
  font-size: 0.85rem;
  line-height: 1;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
}

.rte-btn:hover:not(:disabled) {
  background: rgba(128, 128, 128, 0.16);
}

.rte-btn--active {
  background: rgba(59, 130, 246, 0.18);
  border-color: rgba(59, 130, 246, 0.45);
}

/* Botão que mantém um painel aberto fica CINZA, não azul.
   
   O azul de `--active` diz "esta formatação está aplicada no cursor"; o cinza diz
   "este botão está pressionado agora". São estados diferentes, e o link pode ter
   os dois ao mesmo tempo — editar um link existente com o painel aberto. Cinza
   depois do azul na folha, para vencer nesse encontro: enquanto o painel está
   aberto, o que importa é que ele está aberto. */
.rte-btn--open {
  background: rgba(128, 128, 128, 0.22);
  border-color: transparent;
}

.rte-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.rte-divider {
  width: 1px;
  height: 1.4rem;
  margin: 0 0.25rem;
  background: rgba(128, 128, 128, 0.3);
}

/* Os painéis de link e de tabela também flutuam, logo abaixo da barra — e por
   isso precisam de fundo OPACO, não translúcido: o texto passa por trás deles. */
.rte-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  /* Só a borda de BAIXO. O painel abre colado à barra de ferramentas, que já tem
     a sua `border-bottom`; as duas ficavam a poucos pixels uma da outra e liam
     como linha dupla acidental. */
  border-bottom: 1px solid rgba(128, 128, 128, 0.25);
  background: var(--rte-bg, var(--surface));
  font-size: 0.8rem;
}

/* Campo VISÍVEL em repouso, por fundo e não por contorno.

   Antes era transparente, e o azul só aparecia no foco. O efeito era um painel
   com retângulos invisíveis: não dava para ver que existem três campos no painel
   de imagem (endereço, texto alternativo e legenda) até clicar em cada um. Quem
   não soubesse que estão ali não os procurava.

   Fundo em vez de contorno mantém a decisão antiga — o painel já é uma faixa
   delimitada, e quatro linhas dentro dela seriam moldura sobre moldura. O que
   muda é só o campo deixar de ser invisível.

   O azul é o mesmo de `.rte-btn--active`, e por ser translúcido funciona no
   claro e no escuro sem segunda regra. */
.rte-input {
  flex: 1 1 16rem;
  height: 1.9rem;
  padding: 0 0.5rem;
  border-radius: 0.35rem;
  border: 1px solid transparent;
  background: rgba(59, 130, 246, 0.12);
  font-size: 0.8rem;
  outline: none;
}

/* O texto alternativo é opcional e curto: `flex-basis` menor que o do endereço
   para os dois caberem na mesma linha quando há espaço, e quebrarem juntos
   quando não há. */
.rte-input--alt {
  flex: 1 1 10rem;
}

/* O foco é o MESMO azul, mais forte.

   Com o campo já azul em repouso, o azul sozinho parou de dizer qual deles está
   ativo — e no painel de imagem, que tem três campos, é justamente isso que
   precisa ficar claro. A distinção passa a ser de intensidade: 0.12 em repouso,
   0.22 no foco. Um degrau só, na mesma cor, em vez de introduzir uma segunda.

   Continua sem anel de propósito. O azul do `:focus` que o navegador mostraria
   não vem dele: vem do plugin do Flowbite, que `@tailwind base` injeta e que casa
   `[type='text']:focus` pintando `border-color` e um `box-shadow` de anel em
   `blue.600`. Por isso `outline: none` sozinho nunca resolveu — o `outline`
   daquela regra já é transparente, e o azul está nas outras duas propriedades. E
   por isso esta regra precisa existir: sem ela não haveria nada deste lado para
   disputar com a do plugin.

   0.22 é o teto útil: mais que isso e o texto digitado começa a competir com o
   fundo. */
.rte-input:focus {
  border-color: transparent;
  box-shadow: none;
  outline: none;
  background: rgba(59, 130, 246, 0.22);
}

/* Ícones do painel do link — mesma caixa dos botões da barra, para o painel não
   introduzir um terceiro tamanho de controle no editor. */
.rte-panel-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.9rem;
  height: 1.9rem;
  border-radius: 0.35rem;
  border: 1px solid transparent;
  background: transparent;
  color: inherit;
  font-size: 0.8rem;
  cursor: pointer;
}

.rte-panel-icon:hover:not(:disabled) {
  background: rgba(128, 128, 128, 0.16);
}

/* Desligado é APAGADO, não escondido: a fileira de três ícones tem de manter a
   mesma forma antes e depois de o link ser colado, senão os botões dançam de
   posição enquanto se digita. */
.rte-panel-icon:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.rte-panel-icon--danger:hover:not(:disabled) {
  background: rgba(185, 28, 28, 0.12);
  color: var(--danger-text);
}

.rte-panel-btn {
  height: 1.9rem;
  padding: 0 0.6rem;
  border-radius: 0.35rem;
  border: 1px solid rgba(128, 128, 128, 0.35);
  background: transparent;
  font-size: 0.78rem;
  cursor: pointer;
}

.rte-panel-btn:hover {
  background: rgba(128, 128, 128, 0.16);
}

.rte-panel-btn--danger {
  color: var(--danger-text);
  border-color: rgba(185, 28, 28, 0.4);
}

.rte-panel-label {
  opacity: 0.7;
}

.rte-panel-error {
  color: var(--danger-text);
  flex-basis: 100%;
}
</style>
